/* eslint-disable consistent-return */
import { Meteor } from 'meteor/meteor';
import { check } from 'meteor/check';
import path from 'path';
import fs from 'fs-extra';
import { isEmpty, isNil } from 'lodash';

import * as missionOps from '../mission-operations';
import Missions from '../Missions';
import Projects from '../../Projects/Projects';
import { getBoundingBox } from '../../Projects/project-operations';
import {
  FeaturePoint,
  FeaturePolygon,
  FeatureLineString,
  FeatureCollectionPoints
} from '../../SchemaUtilities/GeoJSONSchema';

import checkAuthentication from '../../../modules/server/check-authentication';

import { sentWaypointTelObjectSchema } from '../../../modules/waypoint-utilities';
import { deleteFolderRecursiveSync } from '../../../modules/server/file-system';
import handleMethodException from '../../../modules/handle-method-exception';
import { isInsideBbox } from '../../../modules/geojson-utilities';
import addElevationOffline from '../../../modules/server/get-elevation-offline';

export const editMissionSchema = Missions.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'deleted',
  'done'
);
editMissionSchema.extend({ _id: String });

/**
 * Inserts the mission in the database.
 *
 * @async
 * @param {object} mission - The mission object.
 * @returns {string} Return the id of the new mission.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insertMission = mission => {
  try {
    checkAuthentication();
    Missions.schema.validate(mission, {
      check,
      keys: ['name', 'project', 'rpa', 'missionType', 'description', 'payload']
    });
    const newMissionID = Missions.insert({ owner: Meteor.userId(), ...mission });
    const newMissionDir = path.join(
      Meteor.settings.private.config.dataPath,
      'users',
      Meteor.userId(),
      'projects',
      mission.project,
      'missions',
      newMissionID
    );
    fs.ensureDirSync(path.join(newMissionDir, 'logs'));
    fs.ensureDirSync(path.join(newMissionDir, 'images'));
    return newMissionID;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the mission in the database for that user, does not copy files in that mission.
 *
 * @param {string} missionId - The mission id.
 * @returns {string} Return the id of the new mission.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const copyMission = missionId => {
  check(missionId, String);
  try {
    checkAuthentication();
    const dupMission = Missions.findOne({ _id: missionId });
    dupMission.name += ' - copy';
    delete dupMission._id;
    delete dupMission.updatedAt;
    delete dupMission.createdAt;
    delete dupMission.done;
    delete dupMission.deleted;
    const dupMissionID = Missions.insert({ ...dupMission });
    const dupMissionDir = path.join(
      Meteor.settings.private.config.dataPath,
      'users',
      Meteor.userId(),
      'projects',
      dupMission.project,
      'missions',
      dupMissionID
    );
    fs.ensureDirSync(path.join(dupMissionDir, 'logs'));
    fs.ensureDirSync(path.join(dupMissionDir, 'images'));
    return dupMissionID;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the mission in the database for that user.
 * DOES NOT copy files in that mission.
 *
 * @param {object} mission - The project object.
 * @param {string} userId - The user id that this mission belongs to.
 * @returns {string} Return the id of the new mission.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const importMission = (mission, userId) => {
  try {
    checkAuthentication();
    Missions.schema.validate(mission, {
      check,
      keys: ['name', 'project', 'rpa', 'missionType', 'description', 'payload', 'flightPlan']
    });
    const importMissionId = Missions.insert({ owner: userId, ...mission });
    const importMissionDir = path.join(
      Meteor.settings.private.config.dataPath,
      'users',
      userId,
      'projects',
      mission.project,
      'missions',
      importMissionId
    );
    fs.ensureDirSync(path.join(importMissionDir, 'logs'));
    fs.ensureDirSync(path.join(importMissionDir, 'images'));
    return importMissionId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the mission in the database.
 *
 * @param {object} mission - The mission object.
 * @returns {string} Return the id of the updated mission.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateMission = mission => {
  try {
    checkAuthentication();
    editMissionSchema.validate(mission, { check });
    const missionId = missionOps.getId(mission);
    Missions.update(missionId, { $set: mission });
    return missionId; // Return _id so we can redirect to document after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the mission in the database.
 *
 * @param {string} missionId - The mission id.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDeleteMission = missionId => {
  check(missionId, String);
  try {
    checkAuthentication();
    Missions.update(missionId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the mission in the database.
 *
 * @param {string} missionId - The mission id.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restoreMission = missionId => {
  check(missionId, String);
  try {
    checkAuthentication();
    Missions.update(missionId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Hard delete the mission in the database.
 *
 * @param {string} missionId - The mission id.
 * @returns {object} { deletedFiles, deletedFolders } Object with these properties.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDeleteMission = missionId => {
  check(missionId, String);
  try {
    checkAuthentication();
    const mission = Missions.findOne({ _id: missionId });
    const missionDir = path.join(
      Meteor.settings.private.config.dataPath,
      'users',
      Meteor.userId(),
      'projects',
      mission.project,
      'missions',
      missionId
    );
    Missions.remove(missionId);
    return deleteFolderRecursiveSync(missionDir);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggle the mission status in the database (done or not).
 *
 * @param {string} missionId - The mission id.
 * @param {boolean} setDone - The new status for the mission.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setDoneMission = (missionId, setDone) => {
  check(missionId, String);
  check(setDone, Boolean);
  try {
    checkAuthentication();
    Missions.update(missionId, { $set: { done: setDone } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Set the take off point for the mission in the database.
 *
 * @param {string} missionId - The mission id.
 * @param {object} takeOffPoint - The new take off point.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setTakeOffPointMission = (missionId, takeOffPoint) => {
  check(missionId, String);
  try {
    checkAuthentication();
    FeaturePoint.validate(takeOffPoint);
    Missions.update(missionId, { $set: { 'flightPlan.takeOffPoint': takeOffPoint } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Set the landing point for the mission in the database.
 *
 * @param {string} missionId - The mission id.
 * @param {object} landingPoint - The new landing point.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setLandingPointMission = (missionId, landingPoint) => {
  check(missionId, String);
  try {
    checkAuthentication();
    FeaturePoint.validate(landingPoint);
    Missions.update(missionId, { $set: { 'flightPlan.landingPoint': landingPoint } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Set the mission geometry in the database,.
 *
 * @param {string} missionId - The mission id.
 * @param {object} [missionGeometry] - The new mission geometry.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setGeometryMission = (missionId, missionGeometry) => {
  check(missionId, String);
  try {
    checkAuthentication();
    const mission = Missions.findOne({ _id: missionId });
    if (isNil(missionGeometry)) {
      if (mission.missionType === 'Surface Area') {
        Missions.update(missionId, { $unset: { 'flightPlan.missionArea': '' } });
      } else if (mission.missionType === 'Linear Area') {
        Missions.update(missionId, { $unset: { 'flightPlan.missionAxis': '' } });
      }
    } else if (mission.missionType === 'Surface Area') {
      FeaturePolygon.validate(missionGeometry);
      Missions.update(missionId, { $set: { 'flightPlan.missionArea': missionGeometry } });
    } else if (mission.missionType === 'Linear Area') {
      FeatureLineString.validate(missionGeometry);
      Missions.update(missionId, { $set: { 'flightPlan.missionAxis': missionGeometry } });
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Set the mission parameters in the database,.
 *
 * @param {string} missionId - The mission id.
 * @param {object} flightParameters - An object representing the mission params.
 * @param {object} pictureGrid - An object that contains the picture grid data.
 * @param {string} waypointReferenceAltitude - The waypoint reference altitude value.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setParamsMission = (
  missionId,
  flightParameters,
  pictureGrid,
  waypointReferenceAltitude
) => {
  check(missionId, String);
  check(waypointReferenceAltitude, String);
  try {
    checkAuthentication();
    const flightParamsSchema = Missions.schema.getObjectSchema('flightPlan.flightParameters');
    flightParamsSchema.validate(flightParameters);
    const pictureGridSchema = Missions.schema.getObjectSchema('flightPlan.pictureGrid');
    pictureGridSchema.validate(pictureGrid);
    Missions.update(missionId, {
      $set: { 'flightPlan.pictureGrid': pictureGrid }
    });
    Missions.update(missionId, {
      $set: { 'flightPlan.flightParameters': flightParameters }
    });
    Missions.update(missionId, {
      $set: { waypointReferenceAltitude }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Saves the new waypoint list in the database.
 *
 * @param {string} missionId - The mission id.
 * @param {object} newWayPointList - An object representing the new waypoint list.
 * @throws {Error} Meteor Error if anything goes wrong;.
 */
export const editWaypointListMission = (missionId, newWayPointList) => {
  check(missionId, String);
  try {
    checkAuthentication();
    FeatureCollectionPoints.validate(newWayPointList);
    Missions.update(missionId, {
      $set: { 'flightPlan.missionCalculation.waypointList': newWayPointList }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Add elevation data to the flight profile from the dtm in the server.
 *
 * @param {string} missionId - The mission id.
 * @param {object} geojson - An geojson object.
 * @returns {object} A geojson object with the altitude added as 3rd coordinate.
 * @throws {Error} If geojson bounding box is outside the dtm bounding box.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const addElevationToGeoJSONMission = async (missionId, geojson) => {
  check(missionId, String);
  try {
    checkAuthentication();
    const mission = Missions.findOne({ _id: missionId });
    const project = Projects.findOne({ _id: mission.project });
    if (isInsideBbox(geojson, getBoundingBox(project))) {
      const geojsonWithElevation = await addElevationOffline(geojson, mission.project);
      return geojsonWithElevation;
    }
    throw Error('geometry outside bbox');
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Save the new elevation profile in the database,
 * if no elevation profile is passed the profile is deleted.
 *
 * @param {string} missionId - The mission id.
 * @param {object} [elevationProfile={}] - The elevation profile for the mission.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const editElevationProfileMission = (missionId, elevationProfile = {}) => {
  check(missionId, String);
  try {
    checkAuthentication();
    if (isEmpty(elevationProfile)) {
      Missions.update(missionId, { $unset: { 'flightPlan.elevationProfile': '' } });
    } else {
      FeatureCollectionPoints.validate(elevationProfile);
      Missions.update(missionId, {
        $set: { 'flightPlan.elevationProfile': elevationProfile }
      });
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Removes all data from the flight plan for that mission in the database.
 *
 * @param {string} missionId - The mission id.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const clearWaypointsMission = missionId => {
  try {
    checkAuthentication();
    Missions.update(missionId, {
      $unset: {
        'flightPlan.takeOffPoint': '',
        'flightPlan.landingPoint': '',
        'flightPlan.missionArea': '',
        'flightPlan.missionAxis': '',
        'flightPlan.missionCalculation.waypointList': '',
        'flightPlan.missionCalculation.missionCalculatedData.totalArea': '',
        'flightPlan.missionCalculation.missionCalculatedData.pathLength': '',
        'flightPlan.missionCalculation.missionCalculatedData.flightTimeMinutes': '',
        'flightPlan.elevationProfile': '',
        'flightPlan.landingBearing': '',
        'flightPlan.waypointListRPA': '',
        'flightPlan.waypointTelemetryArray': ''
      }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Saves the landing bearing in the database.
 *
 * @param {string} missionId - The mission id.
 * @param {object} landingBearing - A linestring representing the landing bearing.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setLandingBearingMission = (missionId, landingBearing) => {
  check(missionId, String);
  try {
    checkAuthentication();
    if (landingBearing) {
      FeatureLineString.validate(landingBearing);
      Missions.update(missionId, { $set: { 'flightPlan.landingBearing': landingBearing } });
    } else if (!landingBearing) {
      Missions.update(missionId, { $unset: { 'flightPlan.landingBearing': '' } });
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Saves the waypoint list as it was when the waypoint list was uploaded to the RPA.
 *
 * @param {string} missionId - The mission id.
 * @param {object} newWayPointList - The waypoint list sent to the rpa.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setFinalWaypointListMission = (missionId, newWayPointList) => {
  check(missionId, String);
  try {
    checkAuthentication();
    if (!isEmpty(newWayPointList)) {
      FeatureCollectionPoints.validate(newWayPointList);
      Missions.update(missionId, {
        $set: { 'flightPlan.waypointListRPA': newWayPointList }
      });
    } else {
      Missions.update(missionId, {
        $unset: { 'flightPlan.waypointListRPA': '' }
      });
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Saves the waypoint list sent to the RPA in RPA format.
 *
 * @param {string} missionId - The mission id.
 * @param {object[]} newWayPointList - The array of waypoints sent to the rpa.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setWaypointTelemetryArray = (missionId, newWayPointList) => {
  check(missionId, String);
  try {
    checkAuthentication();
    if (!isEmpty(newWayPointList)) {
      newWayPointList.forEach(waypoint => {
        sentWaypointTelObjectSchema.validate(waypoint);
      });
      Missions.update(missionId, {
        $set: { 'flightPlan.waypointTelemetryArray': newWayPointList }
      });
    } else {
      Missions.update(missionId, { $unset: { 'flightPlan.waypointTelemetryArray': '' } });
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Save distance travelled and flight time.
 *
 * @param {string} missionId - The mission id.
 * @param {number} newDistanceTraveled - The distance travelled by the rpa.
 * @param {number} newFlightTime - The estimated time the rpa takes to travel that distance at the defined speed.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setDistanceTraveledAndFlightTime = (missionId, newDistanceTraveled, newFlightTime) => {
  check(missionId, String);
  check(newDistanceTraveled, Number);
  check(newFlightTime, Number);
  try {
    checkAuthentication();
    Missions.update(missionId, {
      $set: {
        'flightPlan.missionCalculation.missionCalculatedData.pathLength': newDistanceTraveled
      }
    });
    Missions.update(missionId, {
      $set: {
        'flightPlan.missionCalculation.missionCalculatedData.flightTimeMinutes': newFlightTime
      }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Saves the area that the photogrametric mission span in the database.
 *
 * @param {string} missionId - The mission id.
 * @param {number} newMissionArea - The new area spanned by the mission.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setAreaMission = (missionId, newMissionArea) => {
  check(missionId, String);
  check(newMissionArea, Number);
  try {
    checkAuthentication();
    Missions.update(missionId, {
      $set: { 'flightPlan.missionCalculation.missionCalculatedData.totalArea': newMissionArea }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Saves the picture grid data in the database.
 *
 * @param {string} missionId - The mission id.
 * @param {number} distanceBetweenPics - The distance between pictures in meters.
 * @param {number} timeBetweenPics - The time between camera triggers in seconds.
 * @param {number} groundResolution - The resolution at which the resulting photo will be in cm/pixel.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setPictureGridMission = (
  missionId,
  distanceBetweenPics,
  timeBetweenPics,
  groundResolution
) => {
  check(missionId, String);
  check(distanceBetweenPics, Number);
  check(timeBetweenPics, Number);
  check(groundResolution, Number);
  try {
    checkAuthentication();
    Missions.update(missionId, {
      $set: {
        'flightPlan.missionCalculation.missionCalculatedData.distPics': distanceBetweenPics
      }
    });
    Missions.update(missionId, {
      $set: { 'flightPlan.missionCalculation.missionCalculatedData.shootTime': timeBetweenPics }
    });
    Missions.update(missionId, {
      $set: { 'flightPlan.missionCalculation.missionCalculatedData.resolution': groundResolution }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

export default {
  editMissionSchema,
  insertMission,
  copyMission,
  importMission,
  updateMission,
  softDeleteMission,
  restoreMission,
  hardDeleteMission,
  setDoneMission,
  setTakeOffPointMission,
  setLandingPointMission,
  setGeometryMission,
  setParamsMission,
  editWaypointListMission,
  addElevationToGeoJSONMission,
  editElevationProfileMission,
  clearWaypointsMission,
  setLandingBearingMission,
  setFinalWaypointListMission,
  setWaypointTelemetryArray,
  setDistanceTraveledAndFlightTime,
  setAreaMission,
  setPictureGridMission
};
